import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import MobileBottomNav from "@/pages/dashboard/components/MobileBottomNav";
import CarrierSelector from "./components/CarrierSelector";
import TestHistory from "./components/TestHistory";
import { CarrierTestRecord, CarrierConfig, DEFAULT_CARRIERS } from "./types";
import { supabase } from "@/lib/supabase";
import { useCarrierTestAutoReview } from "@/hooks/useCarrierTestAutoReview";
import useUrlState from "@/hooks/useUrlState";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const STORAGE_KEY = "carrier-test-phones";

// 탭 복귀(리로드) 시 로딩 없이 마지막 내역을 즉시 보여주기 위한 캐시
const RECORDS_CACHE_KEY = "cache:carrier_test_records";

function readRecordsCache(): CarrierTestRecord[] | null {
  try {
    const raw = sessionStorage.getItem(RECORDS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CarrierTestRecord[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeRecordsCache(list: CarrierTestRecord[]): void {
  try {
    sessionStorage.setItem(RECORDS_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패(용량 초과 등)는 무시
  }
}

function loadPhones(): CarrierConfig[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CARRIERS;
    const parsed = JSON.parse(raw) as CarrierConfig[];
    return DEFAULT_CARRIERS.map((d) => {
      const found = parsed.find((p) => p.carrier === d.carrier);
      return found ? { ...d, ...found } : d;
    });
  } catch {
    return DEFAULT_CARRIERS;
  }
}

function savePhones(carriers: CarrierConfig[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(carriers));
}

export default function CarrierTestPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, session } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [carriers, setCarriers] = useState<CarrierConfig[]>(loadPhones);
  const [message, setMessage] = useState("");
  const [liveCount, setLiveCount] = useState(0);
  const [sending, setSending] = useState(false);
  const cachedRecordsRef = useRef<CarrierTestRecord[] | null>(readRecordsCache());
  const [records, setRecords] = useState<CarrierTestRecord[]>(cachedRecordsRef.current ?? []);
  const [loading, setLoading] = useState(cachedRecordsRef.current === null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  // 탭 상태를 주소(URL)에도 남겨 새로고침·링크 공유·탭 복귀에도 유지 (sessionStorage 백업)
  const [historyTabRaw, setHistoryTabRaw] = useUrlState("ct_tab", "grouped");
  const historyTab = (historyTabRaw === "list" ? "list" : "grouped") as "grouped" | "list";
  const setHistoryTab = (t: "grouped" | "list") => setHistoryTabRaw(t);
  const [credit, setCredit] = useState(0);
  const [adminNumbers, setAdminNumbers] = useState<Record<string, string>>();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setLiveCount(message.length);
  }, [message.length]);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // 회원 3사 테스트 실시간 알림 구독
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`user-notifications-${user.id}`)
      .on("broadcast", { event: "carrier-test-review" }, (payload: any) => {
        const data = payload.payload;
        if (data?.carrier && data?.status) {
          const isSuccess = data.status === "success";
          showToast(
            `${data.carrier} 판정 결과: ${isSuccess ? "성공" : "스팸"}`,
            isSuccess ? "success" : "error"
          );
          // 데이터 갱신
          fetchTests();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    savePhones(carriers);
  }, [carriers]);

  // Fetch admin-managed test numbers
  useEffect(() => {
    const fetchAdminNumbers = async () => {
      try {
        const resp = await fetch(`${SUPABASE_URL}/functions/v1/get-carrier-test-numbers`, {
          headers: { apikey: SUPABASE_ANON_KEY },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const adminNums: { carrier: string; phone: string }[] = data.numbers || [];
        const map: Record<string, string> = {};
        adminNums.forEach((n) => {
          if (n.phone?.trim()) map[n.carrier] = n.phone.trim();
        });
        setAdminNumbers(map);

        if (Object.keys(map).length > 0) {
          setCarriers((prev) =>
            prev.map((c) => {
              const adminPhone = map[c.carrier];
              if (adminPhone) {
                return { ...c, phone: adminPhone };
              }
              return c;
            })
          );
        }
      } catch {
        // silently fail
      }
    };
    fetchAdminNumbers();
  }, []);

  // Fetch user credit
  useEffect(() => {
    const fetchCredit = async () => {
      if (!user || !session) return;
      try {
        const resp = await fetch(`${SUPABASE_URL}/functions/v1/get-user-profile`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ user_id: user.id }),
        });
        const data = await resp.json();
        if (data.credit !== undefined) setCredit(data.credit);
      } catch {
        // silently fail
      }
    };
    fetchCredit();
  }, [user, session]);

  const fetchTests = useCallback(async () => {
    if (!user || !session) return;
    // 캐시된 내역이 있으면 로딩 화면 없이 조용히 갱신
    if (cachedRecordsRef.current === null) setLoading(true);
    setFetchError(null);
    try {
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/get-user-carrier-tests`, {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const mapped = (data.tests || []).map((t: any) => ({
        ...t,
        results: Array.isArray(t.results) ? t.results : [],
        review_results: Array.isArray(t.review_results) ? t.review_results : [],
      })) as CarrierTestRecord[];
      setRecords(mapped);
      writeRecordsCache(mapped);
    } catch (err: any) {
      // 갱신 실패 시 기존에 보던 값을 그대로 유지(처음부터 못 불러왔을 때만 빈 화면)
      if (cachedRecordsRef.current === null) setRecords([]);
      setFetchError(err.message || "데이터를 불러오는 중 오류가 발생했습니다.");
    } finally {
      cachedRecordsRef.current = cachedRecordsRef.current ?? [];
      setLoading(false);
    }
  }, [user, session]);

  // Auto-review poller: 사용자가 이 페이지에 머물러 있어도 자동 심사가 돌아가도록
  const pendingCount = records.filter((r) => r.needs_review).length;
  const { triggerNow: triggerAutoReview } = useCarrierTestAutoReview(
    pendingCount > 0,
    fetchTests,
  );

  useEffect(() => {
    if (user && session) fetchTests();
  }, [user, session, fetchTests]);

  const handleSend = async () => {
    if (!session) return;
    // 선택된 통신사만 발송
    const selected = carriers.filter((c) => c.selected && c.phone.trim());
    if (selected.length === 0) {
      showToast("발송할 통신사를 선택하고 수신번호를 입력해주세요.", "error");
      return;
    }
    if (!message.trim()) {
      showToast("메시지 내용을 입력해주세요.", "error");
      return;
    }
    const totalCost = selected.length * 17;
    if (credit < totalCost) {
      showToast(`크레딧이 부족합니다. (필요: ${totalCost}P, 보유: ${credit}P)`, "error");
      return;
    }
    setSending(true);
    try {
      const recipients = selected.map((c) => ({
        carrier: c.carrier,
        phone: c.phone.trim(),
        name: c.carrier,
      }));
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/carrier-test-send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          message: message.trim(),
          type: "SMS",
          recipients,
        }),
      });
      const result = await resp.json();
      if (!resp.ok) {
        if (result.code === "INSUFFICIENT_CREDIT") {
          setCredit(result.current ?? 0);
          throw new Error(`크레딧이 부족합니다. (필요: ${result.required}P, 보유: ${result.current}P)`);
        }
        throw new Error(result.error || `HTTP ${resp.status}`);
      }
      setCredit(result.balance ?? credit);
      showToast(
        `${result.successCount}건 성공${result.failCount > 0 ? `, ${result.failCount}건 실패` : ""} · ${result.cost}P 차감`,
        result.failCount > 0 ? "error" : "success"
      );
      setMessage("");
      setHistoryTab("list");
      await fetchTests();
    } catch (err: any) {
      showToast(err.message || "발송 중 오류가 발생했습니다.", "error");
    } finally {
      setSending(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }
  if (!user) return null;

  const selectedCount = carriers.filter((c) => c.selected && c.phone.trim()).length;
  const totalCost = selectedCount * 17;
  const hasEnoughCredit = credit >= totalCost;
  const hasAdminNumbers = Object.values(adminNumbers ?? {}).some((p) => p.trim());

  return (
    <div className="min-h-screen bg-[#0a0a14] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      <main
        className={`flex-1 transition-all duration-300 ${collapsed ? "md:ml-16" : "md:ml-60"} min-h-screen flex flex-col pb-20 md:pb-0`}
      >
        {/* Top bar */}
        <div className="h-14 md:h-16 flex items-center justify-between px-4 md:px-6 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm">
            <span className="text-gray-500">대시보드</span>
            <i className="ri-arrow-right-s-line text-gray-600 text-xs md:text-sm"></i>
            <span className="text-white font-medium">3사 테스트</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/[0.08]">
              <i className="ri-coin-line text-amber-400 text-xs"></i>
              <span className="text-white text-xs font-semibold">{credit.toLocaleString()}P</span>
            </div>
          </div>
        </div>

        <div className="flex-1 p-4 md:p-6">
          {/* Title */}
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/20">
                <i className="ri-smartphone-line text-base md:text-lg"></i>
              </div>
              <h1 className="text-white text-lg md:text-xl font-bold">3사 테스트</h1>
            </div>
            <p className="text-gray-500 text-xs md:text-sm ml-11 md:ml-12">
              SKT, KT, LGU+ 통신사별 테스트 문자를 발송하세요
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-4 md:gap-6">
            {/* Left: Send form */}
            <div className="rounded-xl bg-[#13131f] border border-white/[0.08] p-4 md:p-5 space-y-4 md:space-y-5 h-fit">
              <h2 className="text-white text-sm font-bold">문자 발송</h2>

              {/* Credit notice */}
              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/15">
                <div className="flex items-center gap-1.5">
                  <i className="ri-information-line text-amber-400 text-xs"></i>
                  <span className="text-gray-400 text-xs">건당 17P 차감</span>
                </div>
                <span className="text-amber-400 text-xs font-semibold">
                  {selectedCount > 0 ? `예상 차감: ${totalCost}P` : "통신사 선택 시 표시"}
                </span>
              </div>

              {!hasAdminNumbers && (
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-500/5 border border-violet-500/15">
                  <i className="ri-information-line text-violet-400 text-xs"></i>
                  <span className="text-gray-400 text-xs">
                    관리자가 수신번호를 등록하지 않았습니다. 직접 입력해주세요.
                  </span>
                </div>
              )}

              <div>
                <CarrierSelector
                  carriers={carriers}
                  onChange={setCarriers}
                  adminNumbers={adminNumbers}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-gray-500 text-xs">메시지 내용 *</label>
                </div>
                <textarea
                  ref={textareaRef}
                  defaultValue={message}
                  onInput={(e) => {
                    const ta = e.target as HTMLTextAreaElement;
                    const raw = ta.value;
                    setLiveCount(raw.length);

                    if (raw.length > 70) {
                      const truncated = raw.slice(0, 70);
                      ta.value = truncated;
                      setLiveCount(70);
                      setMessage(truncated);
                    } else {
                      setMessage(raw);
                    }
                  }}
                  placeholder="메시지를 입력하세요..."
                  rows={6}
                  maxLength={70}
                  className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/40 resize-none"
                />
                <div className="flex items-center justify-between mt-1.5">
                  <span className={`text-[10px] ${liveCount > 70 ? "text-red-400" : "text-gray-500"}`}>{liveCount} / 70자</span>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">
                    SMS
                  </span>
                </div>
              </div>

              <button
                onClick={handleSend}
                disabled={sending || selectedCount === 0 || !message.trim() || !hasEnoughCredit}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 text-white text-sm font-bold hover:from-violet-500 hover:to-pink-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all whitespace-nowrap"
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                    발송 중...
                  </>
                ) : !hasEnoughCredit && selectedCount > 0 ? (
                  <>
                    <i className="ri-coin-line text-sm"></i>
                    크레딧 부족 (필요 {totalCost}P)
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-sm"></i>
                    발송 {selectedCount > 0 && `(${selectedCount}개 통신사 · ${totalCost}P)`}
                  </>
                )}
              </button>
            </div>

            {/* Right: History */}
            <div className="rounded-xl bg-[#13131f] border border-white/[0.08] p-4 md:p-5 space-y-3 md:space-y-4 h-fit">
              <h2 className="text-white text-sm font-bold">발송 내역</h2>
              {loading ? (
                <div className="py-12 flex flex-col items-center justify-center">
                  <i className="ri-loader-4-line animate-spin text-violet-400 text-2xl mb-3"></i>
                  <p className="text-gray-500 text-sm">불러오는 중...</p>
                </div>
              ) : fetchError && records.length === 0 ? (
                <div className="py-12 flex flex-col items-center justify-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 mb-3">
                    <i className="ri-error-warning-line text-red-400 text-lg"></i>
                  </div>
                  <p className="text-gray-400 text-sm">{fetchError}</p>
                  <button
                    onClick={fetchTests}
                    className="mt-3 px-3 py-1.5 rounded-lg bg-violet-600/30 text-violet-300 text-xs hover:bg-violet-600/40 cursor-pointer"
                  >
                    다시 시도
                  </button>
                </div>
              ) : (
                <TestHistory records={records} activeTab={historyTab} onTabChange={setHistoryTab} />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-20 md:bottom-6 right-4 md:right-6 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg z-[100] transition-all ${
            toast.type === "success"
              ? "bg-[#1a1a2e] border-emerald-500/30 text-emerald-400"
              : "bg-[#1a1a2e] border-red-500/30 text-red-400"
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i
              className={`${
                toast.type === "success" ? "ri-checkbox-circle-line" : "ri-close-circle-line"
              } text-base`}
            ></i>
          </div>
          <span className="text-sm">{toast.msg}</span>
        </div>
      )}

      <MobileBottomNav />
    </div>
  );
}